import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Chip, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { IconListDetails } from '@tabler/icons';
import { useLocation } from 'react-router-dom';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';

const TIPO_LABEL = {
    entrada: { label: 'Entrada', color: 'success' },
    saida: { label: 'Saída', color: 'error' },
    ajuste: { label: 'Ajuste', color: 'warning' }
};

const MOTIVO_LABEL = {
    venda: 'Venda',
    cancelamento_venda: 'Cancelamento de Venda',
    compra: 'Compra',
    cancelamento_compra: 'Cancelamento de Compra',
    devolucao: 'Devolução',
    perda: 'Perda/Avaria',
    ajuste_manual: 'Ajuste Manual',
    transferencia: 'Transferência entre Depósitos'
};

const useQuery = () => new URLSearchParams(useLocation().search);

const Kardex = () => {
    const query = useQuery();

    const [appId, setAppId] = useState(null);
    const [products, setProducts] = useState([]);
    const [selectedProductId, setSelectedProductId] = useState('');
    const [selectedVariacaoId, setSelectedVariacaoId] = useState(query.get('produto_variacao') || '');
    const [warehouses, setWarehouses] = useState([]);
    const [warehouseFilter, setWarehouseFilter] = useState(query.get('warehouse') || '');
    const [kardex, setKardex] = useState(null);

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            const [prodRes, whRes] = await Promise.all([
                api.get(`v1/apps/${appId}/products/`),
                api.get(`v1/apps/${appId}/inventory/warehouses/`)
            ]);
            setProducts(prodRes.data || []);
            setWarehouses(whRes.data || []);

            if (selectedVariacaoId) {
                const owner = (prodRes.data || []).find((p) => (p.variacoes || []).some((v) => String(v.id) === String(selectedVariacaoId)));
                if (owner) setSelectedProductId(owner.id);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    useEffect(() => {
        if (!appId || !selectedVariacaoId) {
            setKardex(null);
            return;
        }
        (async () => {
            const params = new URLSearchParams({ produto_variacao: selectedVariacaoId });
            if (warehouseFilter) params.set('warehouse', warehouseFilter);
            const res = await api.get(`v1/apps/${appId}/inventory/kardex/?${params.toString()}`);
            setKardex(res.data);
        })();
    }, [appId, selectedVariacaoId, warehouseFilter]);

    const selectedProduct = products.find((p) => String(p.id) === String(selectedProductId));
    const variacoesDoProduto = selectedProduct?.variacoes || [];

    return (
        <Box>
            <PageHeader
                icon={IconListDetails}
                gradient="graphite"
                title="Kardex"
                subtitle="Ficha cronológica de movimentações com saldo corrente, por produto"
                actions={
                    <Stack direction="row" spacing={2}>
                        <TextField
                            select
                            size="small"
                            label="Produto"
                            value={selectedProductId}
                            onChange={(e) => {
                                setSelectedProductId(e.target.value);
                                setSelectedVariacaoId('');
                            }}
                            sx={{ width: 200 }}
                        >
                            {products.map((p) => (
                                <MenuItem key={p.id} value={p.id}>
                                    {p.nome}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            size="small"
                            label="Variação"
                            disabled={!selectedProductId}
                            value={selectedVariacaoId}
                            onChange={(e) => setSelectedVariacaoId(e.target.value)}
                            sx={{ width: 200 }}
                        >
                            {variacoesDoProduto.map((v) => (
                                <MenuItem key={v.id} value={v.id}>
                                    {v.nome_variacao}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            size="small"
                            label="Depósito"
                            value={warehouseFilter}
                            onChange={(e) => setWarehouseFilter(e.target.value)}
                            sx={{ width: 180 }}
                        >
                            <MenuItem value="">Todos (agregado)</MenuItem>
                            {warehouses.map((w) => (
                                <MenuItem key={w.id} value={w.id}>
                                    {w.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                }
            />

            {!selectedVariacaoId ? (
                <Card>
                    <CardContent>
                        <Typography color="text.secondary">Selecione um produto e uma variação para ver a ficha.</Typography>
                    </CardContent>
                </Card>
            ) : (
                <Card>
                    <CardContent>
                        {kardex && (
                            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    {kardex.produto_variacao_nome}
                                </Typography>
                                {kardex.warehouse_name && <Chip label={kardex.warehouse_name} size="small" variant="outlined" />}
                                <Chip label={`Saldo atual: ${kardex.saldo_atual}`} color="primary" size="small" />
                            </Stack>
                        )}

                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Data</TableCell>
                                    <TableCell>Depósito</TableCell>
                                    <TableCell>Tipo</TableCell>
                                    <TableCell>Motivo</TableCell>
                                    <TableCell align="right">Quantidade</TableCell>
                                    <TableCell align="right">Antes → Depois</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(kardex?.movements || []).map((m) => {
                                    const tipoInfo = TIPO_LABEL[m.tipo] || { label: m.tipo, color: 'default' };
                                    return (
                                        <TableRow key={m.id}>
                                            <TableCell>{new Date(m.created_at).toLocaleString('pt-BR')}</TableCell>
                                            <TableCell>
                                                {m.warehouse_name}
                                                {m.location_code ? ` (${m.location_code})` : ''}
                                                {m.lot_numero && (
                                                    <Chip label={`Lote ${m.lot_numero}`} size="small" variant="outlined" sx={{ ml: 1 }} />
                                                )}
                                                {m.serial_numero && (
                                                    <Chip label={`S/N ${m.serial_numero}`} size="small" variant="outlined" sx={{ ml: 1 }} />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={tipoInfo.label} color={tipoInfo.color} size="small" />
                                            </TableCell>
                                            <TableCell>{MOTIVO_LABEL[m.motivo] || m.motivo}</TableCell>
                                            <TableCell align="right">{m.quantidade}</TableCell>
                                            <TableCell align="right">
                                                {m.quantidade_anterior} → {m.quantidade_posterior}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {kardex && kardex.movements.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            Nenhuma movimentação registrada ainda
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default Kardex;
